import { ColGroupDef } from '@ag-grid-community/core';
import {
  AgGridHelperService,
  ProductColGroupId,
} from '@products/fund-listing/services/ag-grid-helper.service';
import { TranslateService } from '@shared/translate/translate.service';
import { SuppressDetails } from '@types';
import { FundListingPerformanceController } from '../controllers';

/**
 * Return performance column definitions for intl for etfs.
 * @param listingController ppss tab component
 * @param state ppss service state
 * @param agGridHelperService ag grid helper service instance
 * @param translateService translate service instance
 */
export function getPerformanceColumnDefsForUCITS_ETF(
  listingController: FundListingPerformanceController,
  suppressDetails: SuppressDetails,
  agGridHelperService: AgGridHelperService,
  translateService: TranslateService
): ColGroupDef[] {
  const colGroupDefs: ColGroupDef[] = [
    {
      groupId: ProductColGroupId.FUND,
      children: [agGridHelperService.getFundNameColDef()],
    },
    {
      groupId: ProductColGroupId.CURRENCY,
      children: [agGridHelperService.getCurrencyColDef()],
    },
    {
      groupId: ProductColGroupId.NAV,
      headerGroupComponent: 'dateGroupHeaderComponent',
      headerGroupComponentParams: {
        asOfDateText: () => listingController.navAsOfDate,
      },
      children: [
        agGridHelperService.getNavValueColDef(listingController),
        agGridHelperService.getNavChangeValueColDef(listingController),
      ],
    },
    {
      groupId: ProductColGroupId.PERFORMANCE,
      // TODO: to add asOfDate in mobile view
      headerValueGetter: () =>
        `${translateService.instant(
          'pds.PERF_AS_OF_DATE'
        )} ${listingController.getPerformanceAsOf()}`,
      headerGroupComponent: 'performanceGroupHeaderComponent',
      headerGroupComponentParams: listingController,
      children: [
        agGridHelperService.getAvgAnnualMonthly1YrColDef(
          listingController,
          suppressDetails
        ),
        agGridHelperService.getAvgAnnualMonthly3YrColDef(listingController),
        agGridHelperService.getAvgAnnualMonthly5YrColDef(listingController),
        agGridHelperService.getAvgAnnualMonthly10YrColDef(listingController),
        agGridHelperService.getAvgAnnualMonthlyInceptionColDef(
          listingController,
          suppressDetails
        ),
        agGridHelperService.getAvgAnnualQuarterly1YrColDef(
          listingController,
          suppressDetails
        ),
        agGridHelperService.getAvgAnnualQuarterly3YrColDef(listingController),
        agGridHelperService.getAvgAnnualQuarterly5YrColDef(listingController),
        agGridHelperService.getAvgAnnualQuarterly10YrColDef(listingController),
        agGridHelperService.getAvgAnnualQuarterlyInceptionColDef(
          listingController,
          suppressDetails
        ),
        agGridHelperService.getCalendarYearMonthlyYtdColDef(
          listingController,
          suppressDetails
        ),
        agGridHelperService.getCalendarYearMonthly1YrColDef(
          listingController,
          suppressDetails
        ),
        agGridHelperService.getCalendarYearMonthly2YrColDef(listingController),
        agGridHelperService.getCalendarYearMonthly3YrColDef(listingController),
        agGridHelperService.getCalendarYearMonthly4YrColDef(listingController),
        agGridHelperService.getCalendarYearMonthly5YrColDef(listingController),
        agGridHelperService.getCalendarYearQuarterlyYtdColDef(
          listingController,
          suppressDetails
        ),
        agGridHelperService.getCalendarYearQuarterly1YrColDef(
          listingController,
          suppressDetails
        ),
        agGridHelperService.getCalendarYearQuarterly2YrColDef(
          listingController
        ),
        agGridHelperService.getCalendarYearQuarterly3YrColDef(
          listingController
        ),
        agGridHelperService.getCalendarYearQuarterly4YrColDef(
          listingController
        ),
        agGridHelperService.getCalendarYearQuarterly5YrColDef(
          listingController
        ),
        agGridHelperService.getCalendarYearInceptionColDef(),
        agGridHelperService.getCumulativeMonthlyYtdColDef(
          listingController,
          suppressDetails
        ),
        agGridHelperService.getCumulativeMonthly1YrColDef(
          listingController,
          suppressDetails
        ),
        agGridHelperService.getCumulativeMonthly3YrColDef(listingController),
        agGridHelperService.getCumulativeMonthly5YrColDef(listingController),
        agGridHelperService.getCumulativeMonthly10YrColDef(listingController),
        agGridHelperService.getCumulativeMonthlyInceptionColDef(
          listingController,
          suppressDetails
        ),
        agGridHelperService.getCumulativeQuarterlyYtdColDef(
          listingController,
          suppressDetails
        ),
        agGridHelperService.getCumulativeQuarterly1YrColDef(
          listingController,
          suppressDetails
        ),
        agGridHelperService.getCumulativeQuarterly3YrColDef(listingController),
        agGridHelperService.getCumulativeQuarterly5YrColDef(listingController),
        agGridHelperService.getCumulativeQuarterly10YrColDef(listingController),
        agGridHelperService.getCumulativeQuarterlyInceptionColDef(
          listingController,
          suppressDetails
        ),
        agGridHelperService.getDiscreteMonthly1YrColDef(
          listingController,
          suppressDetails
        ),
        agGridHelperService.getDiscreteMonthly2YrColDef(listingController),
        agGridHelperService.getDiscreteMonthly3YrColDef(listingController),
        agGridHelperService.getDiscreteMonthly4YrColDef(listingController),
        agGridHelperService.getDiscreteMonthly5YrColDef(listingController),
        agGridHelperService.getDiscreteQuarterly1YrColDef(
          listingController,
          suppressDetails
        ),
        agGridHelperService.getDiscreteQuarterly2YrColDef(listingController),
        agGridHelperService.getDiscreteQuarterly3YrColDef(listingController),
        agGridHelperService.getDiscreteQuarterly4YrColDef(listingController),
        agGridHelperService.getDiscreteQuarterly5YrColDef(listingController),
        agGridHelperService.getDiscreteInceptionColDef(),
        agGridHelperService.getExpenseRatioUcitsETFColDef(listingController),
        agGridHelperService.getFundDocumentColDef(),
      ],
    },
  ];
  return colGroupDefs;
}
